import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";

import OpenWorkLaneTabs from "../../components/OpenWorkLaneTabs";
import Panel from "../../components/Panel";
import { EmptyState, PageHeader } from "../../components/erp";
import { businessApi } from "../../../services/api";

function money(value) {
  return `₱ ${Number(value || 0).toLocaleString("en-PH")}`;
}

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-PH", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function normalizeOrder(row) {
  return {
    ...row,
    id: row?.id ?? row?._id ?? row?.orderId ?? "—",
    createdAt: row?.createdAt ?? row?.created_at ?? row?.date ?? null,
    total: row?.total ?? row?.grandTotal ?? row?.amount ?? 0,
    status: row?.status ?? "Pending",
    customer:
      row?.customer?.name ||
      row?.customerName ||
      row?.customer ||
      row?.user?.name ||
      row?.user?.email ||
      row?.email ||
      "—",
  };
}

function QueuePanel({ title, subtitle, rows, emptyTitle, emptyDescription }) {
  return (
    <Panel title={title} subtitle={subtitle}>
      {!rows.length ? (
        <EmptyState title={emptyTitle} description={emptyDescription} />
      ) : (
        <div className="divide-y divide-neutral-200 dark:divide-neutral-800">
          {rows.map((order) => (
            <div key={`${title}-${order.id}`} className="py-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                    {order.id} · {order.customer}
                  </div>
                  <div className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                    {formatDateTime(order.createdAt)}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{money(order.total)}</div>
                  <div className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">{order.status}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

export default function OrdersOpenWork() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await businessApi.getOrders();
      const raw = Array.isArray(res)
        ? res
        : Array.isArray(res?.items)
          ? res.items
          : Array.isArray(res?.data)
            ? res.data
            : Array.isArray(res?.data?.items)
              ? res.data.items
              : [];
      setOrders(raw.map(normalizeOrder));
    } catch {
      setOrders([]);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const summary = useMemo(() => {
    const pending = orders.filter((order) => order.status === "Pending");
    const paid = orders.filter((order) => order.status === "Paid");
    const processing = orders.filter((order) => order.status === "Processing");
    const packed = orders.filter((order) => order.status === "Packed");
    const shipped = orders.filter((order) => order.status === "Shipped");
    return {
      pending,
      paid,
      processing,
      packed,
      shipped,
    };
  }, [orders]);
  const lanes = [
    { id: "pending", label: "Pending Orders", count: summary.pending.length },
    { id: "paid", label: "Paid Not Yet Packed", count: summary.paid.length },
    { id: "processing", label: "Processing and Packed", count: summary.processing.length + summary.packed.length },
    { id: "shipped", label: "Shipped Not Yet Delivered", count: summary.shipped.length },
  ];
  const requestedLane = searchParams.get("lane");
  const activeLane = lanes.some((item) => item.id === requestedLane) ? requestedLane : lanes.find((item) => item.count > 0)?.id ?? lanes[0].id;

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Orders"
        title="Orders Open Work"
        subtitle="Review incomplete fulfillment work from a dedicated order action queue."
      />

      <OpenWorkLaneTabs lanes={lanes} active={activeLane} onChange={(nextLane) => setSearchParams({ lane: nextLane })} loading={loading} />

      <div>
        {loading && <div className="rounded-2xl border border-neutral-200 p-6 text-sm text-neutral-500 dark:border-neutral-800 dark:text-neutral-400">Loading order work…</div>}
        {loadError && <div role="alert" className="rounded-2xl border border-danger bg-danger-subtle p-6 text-sm text-danger">Could not load order work. Please try again.</div>}
        {!loading && !loadError && activeLane === "pending" && (
        <QueuePanel
          title="Pending Orders"
          subtitle="Orders still waiting on payment confirmation or first processing action"
          rows={summary.pending.slice(0, 10)}
          emptyTitle="No pending orders"
          emptyDescription="There are no orders in the pending state."
        />
        )}
        {!loading && !loadError && activeLane === "paid" && (
        <QueuePanel
          title="Paid Not Yet Packed"
          subtitle="Orders ready for warehouse or packing follow-through"
          rows={summary.paid.slice(0, 10)}
          emptyTitle="No paid queue"
          emptyDescription="No paid orders are waiting on packing."
        />
        )}
        {!loading && !loadError && activeLane === "processing" && (
        <QueuePanel
          title="Processing and Packed"
          subtitle="Orders actively moving through fulfillment"
          rows={[...summary.processing, ...summary.packed].slice(0, 10)}
          emptyTitle="No active fulfillment queue"
          emptyDescription="No orders are currently in processing or packed states."
        />
        )}
        {!loading && !loadError && activeLane === "shipped" && (
        <QueuePanel
          title="Shipped Not Yet Delivered"
          subtitle="Orders already handed off to delivery"
          rows={summary.shipped.slice(0, 10)}
          emptyTitle="No in-transit orders"
          emptyDescription="No customer orders are currently in shipped status."
        />
        )}
      </div>

      <div className="flex justify-end">
        <Link
          to="/business/orders"
          className="inline-flex items-center gap-2 rounded-xl border border-neutral-200 px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 dark:border-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-900"
        >
          Open orders workspace <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}
